import { useState } from 'react'
import { useTranslation } from 'react-i18next'

type AlertConfig = {
  isOpen: boolean
  header?: string
  subHeader?: string
  buttons: any[]
}

export const ALERT_TYPE = {
  STATUS_UPDATE: 'statusUpdate',
  NOT_LOADED: 'notLoaded',
  FINISHED: 'finished',
}

export type AlertType = (typeof ALERT_TYPE)[keyof typeof ALERT_TYPE]

type UseAlertProps = {
  nextStatus: string | undefined
  onConfirmStatusChange: () => void
  onFinishedRepeatChoice: (repeat: boolean) => void
}

export const useAlert = ({ nextStatus, onConfirmStatusChange, onFinishedRepeatChoice }: UseAlertProps) => {
  const { t } = useTranslation()

  const [alertConfig, setAlertConfig] = useState<AlertConfig>({
    isOpen: false,
    header: undefined,
    subHeader: undefined,
    buttons: [],
  })

  const openAlert = (alertType: AlertType) => {
    switch (alertType) {
      case ALERT_TYPE.STATUS_UPDATE:
        setAlertConfig({
          isOpen: true,
          header: t('mobile.pages.orders.detail.statusChangeButton.alert.header'),
          subHeader: t('mobile.pages.orders.detail.statusChangeButton.alert.subheader', {
            newStatus: t(`general.status.${nextStatus}`),
          }),
          buttons: [
            {
              text: t('mobile.pages.orders.detail.statusChangeButton.alert.cancel'),
              role: 'cancel',
              cssClass: 'secondary',
            },
            {
              text: t('mobile.pages.orders.detail.statusChangeButton.alert.confirm'),
              handler: onConfirmStatusChange,
            },
          ],
        })
        return

      case ALERT_TYPE.NOT_LOADED:
        setAlertConfig({
          isOpen: true,
          header: t('mobile.pages.orders.detail.statusChangeButton.alert.header'),
          subHeader: t('mobile.pages.orders.detail.statusChangeButton.alert.notloaded'),
          buttons: [
            {
              text: t('mobile.pages.orders.detail.statusChangeButton.alert.ok'),
              role: 'cancel',
            },
          ],
        })
        return

      case ALERT_TYPE.FINISHED:
        setAlertConfig({
          isOpen: true,
          header: t('mobile.pages.orders.detail.finishedRepeatButton.alert.header'),
          subHeader: t('mobile.pages.orders.detail.finishedRepeatButton.alert.subheader'),
          buttons: [
            {
              text: t('mobile.pages.orders.detail.finishedRepeatButton.alert.decline'),
              handler: () => onFinishedRepeatChoice(false),
            },
            {
              text: t('mobile.pages.orders.detail.finishedRepeatButton.alert.accept'),
              handler: () => onFinishedRepeatChoice(true),
            },
          ],
        })
        return

      default:
        return
    }
  }

  const closeAlert = () => setAlertConfig((prev) => ({ ...prev, isOpen: false }))

  return {
    openAlert,
    closeAlert,
    alertConfig,
  }
}
